// ./01 - PROJETOS DEV/meu-controle-de-estoque/src/hooks/useSales.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, onSnapshot, doc, updateDoc, writeBatch, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export function useSales(inventory, userId) {
    const [sales, setSales] = useState([]);

    // FUNÇÃO ESCUDO: Protege contra 'Invalid Date'
    const getValidDate = (val, fallback = new Date()) => {
        if (!val) return fallback;
        const d = val instanceof Date ? val : new Date(val);
        return isNaN(d.getTime()) ? fallback : d;
    };

    useEffect(() => {
        if (!userId) {
            setSales([]);
            return;
        }

        const collectionRef = collection(db, 'users', userId, 'sales');
        const unsubscribe = onSnapshot(collectionRef, (snapshot) => {
            const salesData = snapshot.docs.map(doc => {
                const data = doc.data();
                const saleDateTimestamp = data.saleDate || data.date;
                let finalSaleDate = null;
                if (saleDateTimestamp) {
                    const potentialDate = saleDateTimestamp.toDate ? saleDateTimestamp.toDate() : new Date(saleDateTimestamp);
                    if (potentialDate instanceof Date && !isNaN(potentialDate.getTime())) {
                        finalSaleDate = potentialDate;
                    }
                }
                const totalAmount = data.totalAmount ?? data.total ?? 0;
                return {
                    ...data,
                    id: doc.id,
                    saleDate: finalSaleDate,
                    totalAmount: totalAmount,
                    items: data.items || [], 
                    installments: data.installments?.map(inst => ({...inst, dueDate: inst.dueDate?.toDate ? inst.dueDate.toDate() : new Date(inst.dueDate)})) || []
                };
            });
            setSales(salesData);
        });

        return () => unsubscribe();
    }, [userId]);

    const metrics = useMemo(() => {
        const initialMetrics = {
            global: { totalRevenue: 0, totalProfit: 0, avgTicket: 0 },
            byYear: {},
            topProducts: [],
            topBrands: [],
            availableYears: []
        };

        if (!sales.length) return initialMetrics;

        let globalTotalProfit = 0;
        const salesByYearData = {};
        const productSales = new Map();
        const brandSales = new Map();

        const globalTotalRevenue = sales.reduce((acc, sale) => acc + (sale.totalAmount || 0), 0);

        sales.forEach(sale => {
            let saleTotalCost = 0;
            let saleItems = sale.items && sale.items.length > 0 ? sale.items : [{...sale}];

            saleItems.forEach(item => {
                const cost = item.cost || inventory.find(p => p.id === item.productId)?.cost || 0;
                saleTotalCost += cost * (item.quantity || 1);

                const product = inventory.find(p => p.id === item.productId);
                if (product) {
                    brandSales.set(product.brand, (brandSales.get(product.brand) || 0) + (item.unitPrice * item.quantity));
                }
                productSales.set(item.productName, (productSales.get(item.productName) || 0) + (item.unitPrice * item.quantity));
            });

            globalTotalProfit += (sale.totalAmount - saleTotalCost);

            const saleDate = sale.saleDate;
            if (!saleDate) return;
            
            const year = saleDate.getFullYear();
            const month = saleDate.getMonth();
            const profit = sale.totalAmount - saleTotalCost;

            if (!salesByYearData[year]) {
                salesByYearData[year] = { 
                    totalRevenue: 0,
                    totalProfit: 0,
                    salesCount: 0,
                    monthlyRevenue: Array(12).fill(0), 
                    monthlyProfit: Array(12).fill(0) 
                };
            }
            salesByYearData[year].totalRevenue += sale.totalAmount;
            salesByYearData[year].totalProfit += profit;
            salesByYearData[year].salesCount += 1;
            salesByYearData[year].monthlyRevenue[month] += sale.totalAmount;
            salesByYearData[year].monthlyProfit[month] += profit;
        });

        Object.keys(salesByYearData).forEach(year => {
            salesByYearData[year].avgTicket = salesByYearData[year].salesCount > 0 ? salesByYearData[year].totalRevenue / salesByYearData[year].salesCount : 0;
        });

        const availableYears = Object.keys(salesByYearData).sort((a, b) => b - a);

        return {
            global: { totalRevenue: globalTotalRevenue, totalProfit: globalTotalProfit, avgTicket: sales.length > 0 ? globalTotalRevenue / sales.length : 0 },
            byYear: salesByYearData,
            topProducts: [...productSales.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5),
            topBrands: [...brandSales.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5),
            availableYears
        };
    }, [sales, inventory]);

    const financialFlow = useMemo(() => {
        const initial = { receivables: [], receivedHistory: [], byYear: {}, availableYears: [], summary: { toReceiveThisMonth: 0, receivedThisMonth: 0 } };
        if (!sales.length) return initial;

        const financialDataByYear = {};

        const ensureYear = (year) => {
            const yearStr = String(year);
            if (!financialDataByYear[yearStr]) {
                financialDataByYear[yearStr] = { monthlyToReceive: Array(12).fill(0), monthlyReceived: Array(12).fill(0) };
            }
        };

        const receivedHistory = sales.flatMap(sale => 
            (sale.payments || []).map(payment => ({
                ...payment,
                saleId: sale.id,
                customerName: sale.customerName,
                productName: sale.items?.[0]?.productName || 'Venda',
                paymentDate: payment.date?.toDate ? payment.date.toDate() : new Date(payment.date)
            }))
        ).filter(p => !isNaN(p.paymentDate.getTime())) 
         .sort((a, b) => b.paymentDate - a.paymentDate);

        receivedHistory.forEach(item => {
            const year = item.paymentDate.getFullYear();
            const month = item.paymentDate.getMonth();
            ensureYear(year);
            financialDataByYear[String(year)].monthlyReceived[month] += Number(item.amount || 0);
        });

        const receivables = sales.flatMap(sale => 
            (sale.installments || [])
                .filter(inst => inst.status === 'Pendente')
                .map(inst => ({
                    ...inst,
                    saleId: sale.id,
                    customerName: sale.customerName,
                    amountToReceive: Number(inst.amount - (inst.paidAmount || 0)),
                    dueDate: inst.dueDate?.toDate ? inst.dueDate.toDate() : new Date(inst.dueDate)
                }))
        ).sort((a, b) => a.dueDate - b.dueDate);

        receivables.forEach(item => {
            const year = item.dueDate.getFullYear();
            const month = item.dueDate.getMonth();
            ensureYear(year);
            financialDataByYear[String(year)].monthlyToReceive[month] += item.amountToReceive;
        });

        const now = new Date();
        const currentYearStr = String(now.getFullYear());
        const currentMonth = now.getMonth();

        return {
            receivables,
            receivedHistory,
            byYear: financialDataByYear,
            availableYears: Object.keys(financialDataByYear).sort((a, b) => b - a),
            summary: { 
                toReceiveThisMonth: financialDataByYear[currentYearStr]?.monthlyToReceive[currentMonth] || 0, 
                receivedThisMonth: financialDataByYear[currentYearStr]?.monthlyReceived[currentMonth] || 0 
            }
        };
    }, [sales]);

    const addSale = useCallback(async (saleData) => {
        if (!userId) return;
        if (!saleData.items || saleData.items.length === 0) {
            throw new Error("A venda deve conter pelo menos um item.");
        }
        
        const batch = writeBatch(db);
        const saleTotal = Number(saleData.totalAmount || saleData.total || 0); 
        
        for (const item of saleData.items) {
            const productRef = doc(db, 'users', userId, 'products', item.productId);
            const productDoc = await getDoc(productRef);
            if (productDoc.exists()) {
                const currentSoldQty = productDoc.data().soldQty || 0;
                batch.update(productRef, { soldQty: currentSoldQty + Number(item.quantity) });
            }
        }
        
        const newSaleRef = doc(collection(db, 'users', userId, 'sales'));
        const isCashSale = saleData.paymentType === 'À Vista';
        
        let paymentDateObj = new Date();
        if (isCashSale && saleData.installments && saleData.installments.length > 0) {
            // Usa a blindagem para garantir que a data do pagamento nunca seja inválida
            paymentDateObj = getValidDate(saleData.installments[0].dueDate);
        }

        const safeSaleDate = getValidDate(saleData.saleDate);

        const processedSale = {
            ...saleData,
            totalAmount: saleTotal,
            saleDate: safeSaleDate,
            createdAt: serverTimestamp(),
            payments: isCashSale ? [{
                id: crypto.randomUUID(),
                amount: saleTotal,
                date: paymentDateObj, 
                method: saleData.paymentMethod, 
                status: 'pago'
            }] : []
        };

        batch.set(newSaleRef, processedSale);
        await batch.commit();
    }, [userId]);

    const deleteSale = useCallback(async (saleId) => {
        if (!userId) return;
        const saleToDelete = sales.find(s => s.id === saleId);
        if (!saleToDelete) return;

        const batch = writeBatch(db);
        const itemsToUpdate = saleToDelete.items && saleToDelete.items.length > 0 ? saleToDelete.items : [{...saleToDelete}];
        
        for (const item of itemsToUpdate) {
            if (item.productId && item.quantity > 0) {
                const productRef = doc(db, 'users', userId, 'products', item.productId);
                const product = inventory.find(p => p.id === item.productId);
                if (product) {
                    const newSoldQty = Math.max(0, (product.soldQty || 0) - item.quantity);
                    batch.update(productRef, { soldQty: newSoldQty });
                }
            }
        }
        
        const saleRef = doc(db, 'users', userId, 'sales', saleId);
        batch.delete(saleRef);
        
        try {
            await batch.commit();
        } catch (error) {
            console.error("Erro ao deletar venda:", error);
            throw error;
        }
    }, [sales, inventory, userId]);

    const deleteMultipleSales = useCallback(async (saleIds) => {
        if (!userId || !saleIds || saleIds.length === 0) return;
        
        const batch = writeBatch(db);
        saleIds.forEach(saleId => {
            const saleToDelete = sales.find(s => s.id === saleId);
            if (saleToDelete) {
                const itemsToUpdate = saleToDelete.items && saleToDelete.items.length > 0 ? saleToDelete.items : [{...saleToDelete}];
                itemsToUpdate.forEach(item => {
                    if (item.productId && item.quantity > 0) {
                        const productRef = doc(db, 'users', userId, 'products', item.productId);
                        const product = inventory.find(p => p.id === item.productId);
                        if (product) {
                            const newSoldQty = Math.max(0, (product.soldQty || 0) - item.quantity);
                            batch.update(productRef, { soldQty: newSoldQty });
                        }
                    }
                });
                const saleRef = doc(db, 'users', userId, 'sales', saleId);
                batch.delete(saleRef);
            }
        });

        try {
            await batch.commit();
        } catch (error) {
            console.error("Erro ao deletar vendas em lote:", error);
            throw error;
        }
    }, [sales, inventory, userId]);

    const addPaymentToSale = useCallback(async (saleId, paymentAmount, paymentMethod, dateOfPayment) => {
        if (!userId) return;
        
        const saleRef = doc(db, 'users', userId, 'sales', saleId);
        const sale = sales.find(s => s.id === saleId);
        if (!sale) throw new Error("Venda não encontrada");
        
        let paymentLeftToApply = paymentAmount;
        const updatedInstallments = JSON.parse(JSON.stringify(sale.installments));
        
        for (const inst of updatedInstallments) {
            if (inst.status === 'Pendente' && paymentLeftToApply > 0) {
                const alreadyPaid = inst.paidAmount || 0;
                const amountOwed = inst.amount - alreadyPaid;
                
                if (paymentLeftToApply >= amountOwed) {
                    paymentLeftToApply -= amountOwed;
                    inst.paidAmount = inst.amount;
                    inst.status = 'Paga';
                } else {
                    inst.paidAmount = alreadyPaid + paymentLeftToApply;
                    paymentLeftToApply = 0;
                }
            }
        }
        
        const safePaymentDate = getValidDate(dateOfPayment);

        const newPaymentRecord = {
            amount: paymentAmount,
            date: safePaymentDate,
            method: paymentMethod,
        };

        const updatedPayments = [...(sale.payments || []), newPaymentRecord];
        const allInstallmentsPaid = updatedInstallments.every(inst => inst.status === 'Paga');
        const newSaleStatus = allInstallmentsPaid ? 'Concluída' : 'Pendente';
        
        try {
            await updateDoc(saleRef, {
                installments: updatedInstallments,
                payments: updatedPayments,
                status: newSaleStatus,
            });
        } catch(error) {
            console.error("Erro ao adicionar pagamento:", error);
            throw error;
        }
    }, [sales, userId]);

    return { 
        sales,
        metrics,
        financialFlow,
        addSale, 
        deleteSale,
        addPaymentToSale,
        deleteMultipleSales
    };
}